import type {
  CheckoutSplitAllocationMode,
  CheckoutSplitManualPaymentMethod,
  CheckoutSplitPlanReadModel,
  CheckoutSplitShareDraft,
  OperatorSessionReadModel,
  OrderDetail,
  SaveCheckoutSplitPlanRequest,
  TableSnapshot,
} from "@billiard/contracts";
import {
  Banknote,
  Check,
  CircleDollarSign,
  LockKeyhole,
  Merge,
  Plus,
  Save,
  Trash2,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getCheckoutSplitPlan, payCheckoutSplitShare, saveCheckoutSplitPlan } from "./api";
import { businessErrorMessage } from "./businessErrorMessage";
import {
  beginCheckoutSplitDispatch,
  type CheckoutSplitRecovery,
  type CheckoutSplitRecoveryCommand,
  type CheckoutSplitRecoveryView,
  checkoutSplitOutcomeIsUnknown,
  checkoutSplitRecoveryContext,
  checkoutSplitRecoveryUiState,
  claimCheckoutSplitRecovery,
  clearCheckoutSplitRecovery,
  finishCheckoutSplitDispatch,
  markCheckoutSplitOutcomeUnknown,
  readCheckoutSplitRecovery,
  reconcileCheckoutSplitAuthority,
} from "./checkoutSplitRecovery";

type OperatorRole = OperatorSessionReadModel["operator"]["role"];

type SplitOrder = OrderDetail;

interface EditableShare {
  shareKey: string;
  payerLabel: string;
  amountYuan: string;
  otherAmountYuan: string;
  itemQuantities: Record<string, string>;
}

interface DraftValidation {
  shares: CheckoutSplitShareDraft[] | null;
  remainingFen: number | null;
  itemRemaining: Record<string, number | null>;
  messages: string[];
}

const paymentMethodLabels: Record<CheckoutSplitManualPaymentMethod, string> = {
  CASH: "现金",
  STATIC_WECHAT: "微信收款码",
  STATIC_ALIPAY: "支付宝收款码",
};

function formatFen(value: number): string {
  return `¥${(value / 100).toFixed(2)}`;
}

function fenToYuan(value: number): string {
  return (value / 100).toFixed(2);
}

function yuanToFen(value: string, allowZero = false): number | null {
  const normalized = value.trim();
  if (!/^(?:0|[1-9]\d{0,8})(?:\.\d{1,2})?$/.test(normalized)) return null;
  const [yuan, decimal = ""] = normalized.split(".");
  const fen = Number(yuan) * 100 + Number(decimal.padEnd(2, "0"));
  if (!Number.isSafeInteger(fen) || fen < 0 || (!allowZero && fen === 0)) return null;
  return fen;
}

function integerQuantity(value: string): number | null {
  if (!/^(?:0|[1-9]\d*)$/.test(value.trim())) return null;
  const parsed = Number(value);
  return Number.isSafeInteger(parsed) ? parsed : null;
}

function distribute(total: number, count: number): number[] {
  const base = Math.floor(total / count);
  const remainder = total % count;
  return Array.from({ length: count }, (_, index) => base + (index < remainder ? 1 : 0));
}

function activeQuantity(item: SplitOrder["items"][number]): number {
  return Math.max(0, item.quantity - item.returnedQuantity);
}

function itemSubtotalFen(order: SplitOrder): number | null {
  let total = 0;
  for (const item of order.items) {
    const line = activeQuantity(item) * item.unitPriceFen;
    total += line;
    if (!Number.isSafeInteger(line) || !Number.isSafeInteger(total)) return null;
  }
  return total;
}

let shareSequence = 0;
function nextShareKey(): string {
  shareSequence += 1;
  return `checkout-share-${Date.now()}-${shareSequence}`;
}

function nextIdempotencyKey(prefix: string): string {
  const random = globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random()}`;
  return `${prefix}:${random}`;
}

function makeShares(
  order: SplitOrder,
  _mode: CheckoutSplitAllocationMode,
  count: number,
  labels?: readonly string[],
): EditableShare[] {
  const amountParts = distribute(order.totalFen, count);
  const itemSubtotal = itemSubtotalFen(order);
  const otherTotal = itemSubtotal === null ? 0 : Math.max(0, order.totalFen - itemSubtotal);
  const otherParts = distribute(otherTotal, count);
  const itemParts = new Map(
    order.items.map((item) => [item.id, distribute(activeQuantity(item), count)] as const),
  );
  return Array.from({ length: count }, (_, index) => ({
    shareKey: nextShareKey(),
    payerLabel: labels?.[index] ?? `顾客${index + 1}`,
    amountYuan: fenToYuan(amountParts[index] ?? 0),
    otherAmountYuan: fenToYuan(otherParts[index] ?? 0),
    itemQuantities: Object.fromEntries(
      order.items.map((item) => [item.id, String(itemParts.get(item.id)?.[index] ?? 0)]),
    ),
  }));
}

function sharesFromPlan(plan: CheckoutSplitPlanReadModel, order: SplitOrder): EditableShare[] {
  return plan.shares.map((share) => ({
    shareKey: share.shareKey,
    payerLabel: share.payerLabel,
    amountYuan: fenToYuan(share.amountFen),
    otherAmountYuan: fenToYuan(share.otherAmountFen),
    itemQuantities: Object.fromEntries(
      order.items.map((item) => [
        item.id,
        String(
          share.itemAllocations.find((allocation) => allocation.itemId === item.id)?.quantity ?? 0,
        ),
      ]),
    ),
  }));
}

export function checkoutSplitDraftValidation(
  order: SplitOrder,
  mode: CheckoutSplitAllocationMode,
  editable: readonly EditableShare[],
): DraftValidation {
  const messages: string[] = [];
  if (editable.length < 2 || editable.length > 8) messages.push("多人分账必须保留 2 至 8 位顾客。");
  const labels = editable.map((share) => share.payerLabel.trim());
  if (labels.some((label) => label.length === 0 || label.length > 30)) {
    messages.push("每位顾客都要填写 1 至 30 个字的称呼。");
  } else if (new Set(labels).size !== labels.length) {
    messages.push("顾客称呼不能重复，请加入座位号或其他容易核对的信息。");
  }

  const itemRemaining: Record<string, number | null> = {};
  const itemQuantitiesByShare = editable.map(() => new Map<string, number>());
  if (mode === "BY_ITEM") {
    for (const item of order.items) {
      const quantities = editable.map((share) =>
        integerQuantity(share.itemQuantities[item.id] ?? "0"),
      );
      if (quantities.some((quantity) => quantity === null)) {
        itemRemaining[item.id] = null;
        messages.push(`“${item.name}”的件数必须填写 0 或正整数。`);
        continue;
      }
      const allocated = (quantities as number[]).reduce((sum, quantity) => sum + quantity, 0);
      const remaining = activeQuantity(item) - allocated;
      itemRemaining[item.id] = remaining;
      if (remaining !== 0) {
        messages.push(
          remaining > 0
            ? `“${item.name}”还剩 ${remaining} 件没有分配。`
            : `“${item.name}”多分了 ${Math.abs(remaining)} 件。`,
        );
      }
      quantities.forEach((quantity, index) => {
        if (quantity && quantity > 0) itemQuantitiesByShare[index]?.set(item.id, quantity);
      });
    }
  }

  const itemSubtotal = itemSubtotalFen(order);
  const otherTotalFen = itemSubtotal === null ? null : order.totalFen - itemSubtotal;
  if (mode === "BY_ITEM" && (otherTotalFen === null || otherTotalFen < 0)) {
    messages.push("商品行金额大于本单应收，当前台单不能安全按商品行分配。");
  }

  const drafts: CheckoutSplitShareDraft[] = [];
  let allocatedFen = 0;
  editable.forEach((share, index) => {
    const itemAllocations = [...(itemQuantitiesByShare[index] ?? new Map())].map(
      ([itemId, quantity]) => ({ itemId, quantity }),
    );
    const amountFen =
      mode === "BY_AMOUNT"
        ? yuanToFen(share.amountYuan)
        : (() => {
            const other = yuanToFen(share.otherAmountYuan, true);
            if (other === null) return null;
            let total = other;
            for (const allocation of itemAllocations) {
              const item = order.items.find((candidate) => candidate.id === allocation.itemId);
              if (!item) return null;
              total += item.unitPriceFen * allocation.quantity;
              if (!Number.isSafeInteger(total)) return null;
            }
            return total;
          })();
    const otherAmountFen =
      mode === "BY_AMOUNT" ? amountFen : yuanToFen(share.otherAmountYuan, true);
    if (amountFen === null || amountFen <= 0 || otherAmountFen === null) {
      messages.push(
        mode === "BY_AMOUNT"
          ? `${labels[index] || `第 ${index + 1} 位顾客`}的金额必须大于 0，且最多保留两位小数。`
          : `${labels[index] || `第 ${index + 1} 位顾客`}分到的商品和其他金额合计必须大于 0。`,
      );
      return;
    }
    allocatedFen += amountFen;
    drafts.push({
      shareKey: share.shareKey,
      payerLabel: labels[index] ?? share.payerLabel,
      amountFen,
      otherAmountFen,
      itemAllocations,
    });
  });

  const remainingFen = Number.isSafeInteger(allocatedFen) ? order.totalFen - allocatedFen : null;
  if (remainingFen !== 0) {
    messages.push(
      remainingFen === null
        ? "分账金额过大，无法安全计算。"
        : remainingFen > 0
          ? `全单还剩 ${formatFen(remainingFen)} 没有分配。`
          : `全单多分了 ${formatFen(Math.abs(remainingFen))}。`,
    );
  }
  if (mode === "BY_ITEM" && otherTotalFen !== null && otherTotalFen >= 0) {
    const otherAllocated = drafts.reduce((sum, share) => sum + share.otherAmountFen, 0);
    const otherRemaining = otherTotalFen - otherAllocated;
    if (otherRemaining !== 0) {
      messages.push(
        otherRemaining > 0
          ? `台费、助教及其他金额还剩 ${formatFen(otherRemaining)} 没有分配。`
          : `台费、助教及其他金额多分了 ${formatFen(Math.abs(otherRemaining))}。`,
      );
    }
  }

  return {
    shares: messages.length === 0 && drafts.length === editable.length ? drafts : null,
    remainingFen,
    itemRemaining,
    messages: [...new Set(messages)],
  };
}

function paymentStateCopy(method: CheckoutSplitManualPaymentMethod | null): string {
  if (method === "CASH") return "已付款 · 现金当面确认";
  if (method === "STATIC_WECHAT") return "已付款 · 微信收款码待渠道核对";
  if (method === "STATIC_ALIPAY") return "已付款 · 支付宝收款码待渠道核对";
  return "已付款";
}

function recoveryCommandCopy(command: CheckoutSplitRecoveryCommand): string {
  if (command.kind === "SAVE_PLAN") {
    return `原请求：保存 ${command.request.shares.length} 份分账计划。`;
  }
  const payerLabel =
    command.baseline.baselinePlan.shares.find((share) => share.shareId === command.request.shareId)
      ?.payerLabel ?? "指定顾客";
  return `原请求：${payerLabel}使用${paymentMethodLabels[command.request.method]}支付 ${formatFen(
    command.request.expectedShareAmountFen,
  )}。`;
}

export type CheckoutKind = "WHOLE" | "SPLIT" | "COMBINED";

export function CheckoutKindSelector({
  value,
  wholeDisabled = false,
  splitDisabled,
  combinedDisabled = false,
  onChange,
}: {
  value: CheckoutKind;
  wholeDisabled?: boolean;
  splitDisabled: boolean;
  combinedDisabled?: boolean;
  onChange: (next: CheckoutKind) => void;
}): React.JSX.Element {
  return (
    <fieldset className="checkout-kind-selector">
      <legend>选择结账方式</legend>
      <button
        className={value === "WHOLE" ? "is-active" : ""}
        type="button"
        aria-pressed={value === "WHOLE"}
        disabled={wholeDisabled}
        onClick={() => onChange("WHOLE")}
      >
        <strong>普通整单</strong>
        <small>一位顾客用一种方式付清，或使用已有会员组合支付。</small>
      </button>
      <button
        className={value === "SPLIT" ? "is-active" : ""}
        type="button"
        aria-pressed={value === "SPLIT"}
        disabled={splitDisabled}
        onClick={() => onChange("SPLIT")}
      >
        <strong>多人分账</strong>
        <small>2 至 8 位顾客按金额或商品行逐份付款。</small>
      </button>
      <button
        className={value === "COMBINED" ? "is-active" : ""}
        type="button"
        aria-pressed={value === "COMBINED"}
        disabled={combinedDisabled}
        onClick={() => onChange("COMBINED")}
      >
        <strong>跨桌合并</strong>
        <small>2 至 4 张已停表、未付款台单一次收款。</small>
      </button>
    </fieldset>
  );
}

export function CheckoutSplitPanel({
  order,
  table,
  operator,
  effectiveRoles,
  disabled,
  onCompleted,
  onPlanChanged,
  onPlanLockedChange,
}: {
  order: SplitOrder;
  table: TableSnapshot;
  operator: { tenantId: string; storeId: string; id: string };
  effectiveRoles: readonly OperatorRole[];
  disabled: boolean;
  onCompleted?: () => void;
  onPlanChanged?: () => void;
  onPlanLockedChange?: (locked: boolean, focusSplit: boolean) => void;
}): React.JSX.Element {
  const canCollect = effectiveRoles.includes("CASHIER") || effectiveRoles.includes("MANAGER");
  const recoveryContext = useMemo(
    () =>
      checkoutSplitRecoveryContext(
        { tenantId: operator.tenantId, storeId: operator.storeId, id: operator.id },
        order.sessionId,
      ),
    [operator.id, operator.storeId, operator.tenantId, order.sessionId],
  );
  const [mode, setMode] = useState<CheckoutSplitAllocationMode>("BY_AMOUNT");
  const [editable, setEditable] = useState<EditableShare[]>(() =>
    makeShares(order, "BY_AMOUNT", 2),
  );
  const [plan, setPlan] = useState<CheckoutSplitPlanReadModel | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [payingShareId, setPayingShareId] = useState<string | null>(null);
  const [dirty, setDirty] = useState(true);
  const [feedback, setFeedback] = useState<{
    kind: "SUCCESS" | "ERROR" | "UNKNOWN";
    message: string;
  } | null>(null);
  const [methods, setMethods] = useState<Record<string, CheckoutSplitManualPaymentMethod>>({});
  const [confirmed, setConfirmed] = useState<Record<string, boolean>>({});
  const [authorityTrusted, setAuthorityTrusted] = useState(false);
  const [recoveryView, setRecoveryView] = useState<CheckoutSplitRecoveryView>(() =>
    readCheckoutSplitRecovery(recoveryContext),
  );
  const locked = Boolean(plan && plan.status !== "DRAFT");
  const orderRef = useRef(order);
  const completedRef = useRef(onCompleted);
  const planChangedRef = useRef(onPlanChanged);
  orderRef.current = order;
  completedRef.current = onCompleted;
  planChangedRef.current = onPlanChanged;

  const applyPlan = useCallback((next: CheckoutSplitPlanReadModel) => {
    setPlan(next);
    setMode(next.allocationMode);
    setEditable(sharesFromPlan(next, orderRef.current));
    setDirty(false);
    setConfirmed({});
  }, []);

  const recoveryUi = checkoutSplitRecoveryUiState(recoveryView);
  const baselineStale = Boolean(plan && order.paidFen !== plan.paidFen);
  const authorityUnavailable = !loading && !authorityTrusted;
  const wholeCheckoutLocked =
    loading || authorityUnavailable || plan !== null || recoveryView.status !== "NONE";
  const splitNeedsAttention =
    authorityUnavailable || plan !== null || recoveryView.status !== "NONE";

  useEffect(() => {
    onPlanLockedChange?.(wholeCheckoutLocked, splitNeedsAttention);
  }, [onPlanLockedChange, splitNeedsAttention, wholeCheckoutLocked]);

  const applyEmptyDraft = useCallback(() => {
    setPlan(null);
    setMode("BY_AMOUNT");
    setEditable(makeShares(orderRef.current, "BY_AMOUNT", 2));
    setDirty(true);
    setConfirmed({});
  }, []);

  const reconcileRecovery = useCallback(
    (authoritativePlan: CheckoutSplitPlanReadModel | null): void => {
      if (authoritativePlan) applyPlan(authoritativePlan);
      else applyEmptyDraft();

      const current = readCheckoutSplitRecovery(recoveryContext);
      if (current.status !== "OWNED" || current.recovery.state !== "OUTCOME_UNKNOWN") {
        setRecoveryView(current);
        if (current.status !== "NONE") {
          const ui = checkoutSplitRecoveryUiState(current);
          setFeedback({
            kind: current.status === "OWNED" ? "UNKNOWN" : "ERROR",
            message: ui.message ?? "上一笔多人分账操作尚未安全收口，当前页面已停止新的分账操作。",
          });
          if (current.status === "OWNED" && current.recovery.command.kind === "PAY_SHARE") {
            const request = current.recovery.command.request;
            setMethods((methodsByShare) => ({
              ...methodsByShare,
              [request.shareId]: request.method,
            }));
          }
        }
        return;
      }

      const commandKind = current.recovery.command.kind;
      const resolution = reconcileCheckoutSplitAuthority(
        current.recovery,
        recoveryContext,
        authoritativePlan,
      );
      const nextView = readCheckoutSplitRecovery(recoveryContext);
      setRecoveryView(nextView);
      if (resolution.status === "EXECUTED") {
        setFeedback({
          kind: "SUCCESS",
          message:
            commandKind === "SAVE_PLAN"
              ? "已从门店权威记录确认：上一份分账计划已经保存，本地恢复记录已收口。"
              : "已从门店权威记录确认：上一位顾客已经付款，不会再次收取，本地恢复记录已收口。",
        });
        if (commandKind === "PAY_SHARE") {
          planChangedRef.current?.();
          if (authoritativePlan?.status === "COMPLETED") completedRef.current?.();
        }
        return;
      }
      if (resolution.status === "RETRY_ALLOWED") {
        if (resolution.recovery.command.kind === "PAY_SHARE") {
          const request = resolution.recovery.command.request;
          setMethods((methodsByShare) => ({
            ...methodsByShare,
            [request.shareId]: request.method,
          }));
        }
        setFeedback({
          kind: "UNKNOWN",
          message:
            "上一笔结果曾经未知；门店权威记录现已明确证明原请求没有执行。只能使用下方原请求号安全重试，不能改内容或新建请求。",
        });
        return;
      }
      setFeedback({
        kind: resolution.status === "STILL_UNKNOWN" ? "UNKNOWN" : "ERROR",
        message:
          resolution.status === "STILL_UNKNOWN"
            ? "已读取门店权威记录，但仍无法证明上一笔执行或未执行。页面继续锁定，请稍后重新读取，不能改内容、换账号或新建请求。"
            : "门店权威记录与本地恢复记录无法安全对应，页面已失败关闭；请保留本页并由店长核对。",
      });
    },
    [applyEmptyDraft, applyPlan, recoveryContext],
  );

  const loadAuthoritativePlan = useCallback(async (): Promise<void> => {
    setLoading(true);
    setAuthorityTrusted(false);
    try {
      const authoritativePlan = await getCheckoutSplitPlan(order.sessionId);
      reconcileRecovery(authoritativePlan);
      setAuthorityTrusted(true);
    } catch (error) {
      setRecoveryView(readCheckoutSplitRecovery(recoveryContext));
      setFeedback({
        kind: "ERROR",
        message: `多人分账权威状态读取失败：${businessErrorMessage(error, "系统没有返回明确原因")}。在读取成功前，页面继续锁定，不能保存或收款。`,
      });
    } finally {
      setLoading(false);
    }
  }, [order.sessionId, reconcileRecovery, recoveryContext]);

  useEffect(() => {
    setFeedback(null);
    void loadAuthoritativePlan();
  }, [loadAuthoritativePlan]);

  const validation = useMemo(
    () => checkoutSplitDraftValidation(order, mode, editable),
    [editable, mode, order],
  );
  const savedShareByKey = new Map(plan?.shares.map((share) => [share.shareKey, share]) ?? []);
  const operationDisabled =
    disabled ||
    !canCollect ||
    loading ||
    !authorityTrusted ||
    saving ||
    payingShareId !== null ||
    recoveryView.status !== "NONE" ||
    baselineStale;

  const changeDraft = (next: EditableShare[]): void => {
    setEditable(next);
    setDirty(true);
    setFeedback(null);
    setConfirmed({});
  };

  const updateShare = (shareKey: string, update: Partial<EditableShare>): void => {
    changeDraft(
      editable.map((share) => (share.shareKey === shareKey ? { ...share, ...update } : share)),
    );
  };

  const switchMode = (nextMode: CheckoutSplitAllocationMode): void => {
    if (nextMode === mode || locked) return;
    setMode(nextMode);
    changeDraft(
      makeShares(
        order,
        nextMode,
        editable.length,
        editable.map((share) => share.payerLabel),
      ),
    );
  };

  const mergeIntoPrevious = (index: number): void => {
    const source = editable[index];
    const target = editable[index - 1];
    if (!source || !target || editable.length <= 2) return;
    const targetAmount = yuanToFen(target.amountYuan, true);
    const sourceAmount = yuanToFen(source.amountYuan, true);
    const targetOther = yuanToFen(target.otherAmountYuan, true);
    const sourceOther = yuanToFen(source.otherAmountYuan, true);
    if (
      targetAmount === null ||
      sourceAmount === null ||
      targetOther === null ||
      sourceOther === null
    ) {
      setFeedback({ kind: "ERROR", message: "请先修正这两份中的金额，再执行合并。" });
      return;
    }
    const merged: EditableShare = {
      ...target,
      amountYuan: fenToYuan(targetAmount + sourceAmount),
      otherAmountYuan: fenToYuan(targetOther + sourceOther),
      itemQuantities: Object.fromEntries(
        order.items.map((item) => [
          item.id,
          String(
            (integerQuantity(target.itemQuantities[item.id] ?? "0") ?? 0) +
              (integerQuantity(source.itemQuantities[item.id] ?? "0") ?? 0),
          ),
        ]),
      ),
    };
    changeDraft(
      editable
        .filter((_, itemIndex) => itemIndex !== index)
        .map((share, itemIndex) => (itemIndex === index - 1 ? merged : share)),
    );
  };

  const dispatchRecovery = async (recovery: CheckoutSplitRecovery): Promise<void> => {
    if (disabled || !canCollect || saving || payingShareId !== null) return;
    if (!beginCheckoutSplitDispatch(recovery, recoveryContext)) {
      const view = readCheckoutSplitRecovery(recoveryContext);
      setRecoveryView(view);
      setFeedback({
        kind: "ERROR",
        message:
          checkoutSplitRecoveryUiState(view).message ??
          "这笔多人分账原请求当前不能安全提交，页面已停止新的操作。",
      });
      return;
    }

    const command = recovery.command;
    const payingShare = command.kind === "PAY_SHARE" ? command.request.shareId : null;
    const payerLabel =
      command.kind === "PAY_SHARE"
        ? (command.baseline.baselinePlan.shares.find(
            (share) => share.shareId === command.request.shareId,
          )?.payerLabel ?? "这位顾客")
        : null;
    if (payingShare) setPayingShareId(payingShare);
    else setSaving(true);
    setFeedback(null);

    try {
      const result =
        recovery.command.kind === "SAVE_PLAN"
          ? await saveCheckoutSplitPlan(
              recovery.sessionId,
              recovery.command.request,
              recovery.command.baseline,
            )
          : await payCheckoutSplitShare(
              recovery.sessionId,
              recovery.command.request,
              recovery.command.baseline,
            );
      const cleared = clearCheckoutSplitRecovery(recovery, recoveryContext);
      setRecoveryView(readCheckoutSplitRecovery(recoveryContext));
      applyPlan(result);
      if (!cleared) {
        setFeedback({
          kind: "ERROR",
          message:
            "门店已经返回成功结果，但本机无法安全清除恢复记录。页面已失败关闭，请保留本页并由店长核对。",
        });
        return;
      }
      if (payingShare) {
        planChangedRef.current?.();
        setFeedback({
          kind: "SUCCESS",
          message:
            result.status === "COMPLETED"
              ? "全部顾客均已付款，台单已结账，球台进入待清台。"
              : `${payerLabel}已付款；其余未付款份额继续逐份收取，分账计划已锁定不可修改。`,
        });
        if (result.status === "COMPLETED") completedRef.current?.();
      } else {
        setFeedback({
          kind: "SUCCESS",
          message: "分账计划已保存。首位顾客付款后，人数、金额和商品分配都会锁定。",
        });
      }
    } catch (error) {
      if (checkoutSplitOutcomeIsUnknown(error)) {
        markCheckoutSplitOutcomeUnknown(recovery, recoveryContext);
        setRecoveryView(readCheckoutSplitRecovery(recoveryContext));
        setFeedback({
          kind: "UNKNOWN",
          message: payingShare
            ? `${payerLabel}的收款结果未知：${businessErrorMessage(error, "系统没有返回明确结果")}。不要让顾客再次付款或更换方式；正在读取门店权威记录。`
            : `保存结果未知：${businessErrorMessage(error, "系统没有返回明确结果")}。不能修改或新建计划；正在读取门店权威记录。`,
        });
        await loadAuthoritativePlan();
      } else {
        clearCheckoutSplitRecovery(recovery, recoveryContext);
        setRecoveryView(readCheckoutSplitRecovery(recoveryContext));
        setFeedback({
          kind: "ERROR",
          message: payingShare
            ? `${payerLabel}当前收款失败：${businessErrorMessage(error, "系统没有返回明确原因")}。系统未把这份标记为已付款，可以核对后再试。`
            : `分账计划保存失败：${businessErrorMessage(error, "系统没有返回明确原因")}。本页仍保留当前输入。`,
        });
      }
    } finally {
      finishCheckoutSplitDispatch(recovery, recoveryContext);
      setSaving(false);
      setPayingShareId(null);
    }
  };

  const claimAndDispatch = (command: CheckoutSplitRecoveryCommand): void => {
    const claimed = claimCheckoutSplitRecovery(recoveryContext, command);
    setRecoveryView(claimed);
    if (claimed.status === "OWNED") {
      void dispatchRecovery(claimed.recovery);
      return;
    }
    setFeedback({
      kind: "ERROR",
      message:
        checkoutSplitRecoveryUiState(claimed).message ??
        "本机无法安全保存这笔多人分账原请求，系统没有发出请求。",
    });
  };

  const savePlan = (): void => {
    if (!validation.shares || operationDisabled || locked) return;
    const request: SaveCheckoutSplitPlanRequest = {
      contractVersion: "1.0.0",
      expectedOrderId: order.id,
      expectedTotalFen: order.totalFen,
      expectedPlanVersion: plan?.version ?? null,
      allocationMode: mode,
      shares: validation.shares,
      idempotencyKey: nextIdempotencyKey("checkout-split-save"),
    };
    claimAndDispatch({
      kind: "SAVE_PLAN",
      request,
      baseline: {
        tenantId: operator.tenantId,
        storeId: operator.storeId,
        baselineOrder: order,
        baselineTable: table,
        baselinePlan: plan,
      },
    });
  };

  const payShare = (shareId: string): void => {
    const currentPlan = plan;
    const share = currentPlan?.shares.find((candidate) => candidate.shareId === shareId);
    const method = methods[shareId] ?? "CASH";
    if (
      !currentPlan ||
      !share ||
      share.status !== "UNPAID" ||
      !confirmed[shareId] ||
      dirty ||
      operationDisabled
    ) {
      return;
    }
    claimAndDispatch({
      kind: "PAY_SHARE",
      request: {
        contractVersion: "1.0.0",
        planId: currentPlan.planId,
        shareId,
        expectedPlanVersion: currentPlan.version,
        expectedShareAmountFen: share.amountFen,
        method,
        idempotencyKey: nextIdempotencyKey(`checkout-split-pay:${share.shareKey}`),
      },
      baseline: {
        tenantId: operator.tenantId,
        storeId: operator.storeId,
        baselineOrder: order,
        baselineTable: table,
        baselinePlan: currentPlan,
      },
    });
  };

  if (!canCollect) {
    return (
      <section className="checkout-split-panel" aria-label="多人分账">
        <output className="disabled-reason">
          当前岗位不能收款或修改多人分账；请由前台收银员或当班店长办理。
        </output>
      </section>
    );
  }

  if (loading) {
    return <div className="loading-state">正在读取本单多人分账状态...</div>;
  }

  return (
    <section className="checkout-split-panel" aria-label="多人分账">
      <header className="checkout-split-heading">
        <div>
          <h3>多人分账</h3>
          <p>本单应收 {formatFen(order.totalFen)}，可拆给 2 至 8 位顾客逐份收款。</p>
        </div>
        {locked ? (
          <span className="checkout-split-lock">
            <LockKeyhole size={15} />
            首笔已付，计划已锁定
          </span>
        ) : null}
      </header>

      <p className="checkout-split-boundary">
        本轮只支持现金、微信收款码和支付宝收款码；不提供会员余额、微信/支付宝正式支付或跨桌合单。
      </p>

      <fieldset className="checkout-split-mode" disabled={operationDisabled || locked}>
        <legend>怎么分</legend>
        <label className={mode === "BY_AMOUNT" ? "is-active" : ""}>
          <input
            type="radio"
            name="checkout-split-allocation-mode"
            checked={mode === "BY_AMOUNT"}
            onChange={() => switchMode("BY_AMOUNT")}
          />
          <span>
            <strong>按金额分</strong>
            <small>直接填写每位顾客应付多少钱。</small>
          </span>
        </label>
        <label className={mode === "BY_ITEM" ? "is-active" : ""}>
          <input
            type="radio"
            name="checkout-split-allocation-mode"
            checked={mode === "BY_ITEM"}
            onChange={() => switchMode("BY_ITEM")}
          />
          <span>
            <strong>按商品行分</strong>
            <small>逐件分商品，再分配台费、助教及其他金额。</small>
          </span>
        </label>
      </fieldset>

      <div className="checkout-split-summary" aria-live="polite">
        <div>
          <span>全单应收</span>
          <strong>{formatFen(order.totalFen)}</strong>
        </div>
        <div className={validation.remainingFen === 0 ? "is-balanced" : "is-unbalanced"}>
          <span>全单剩余</span>
          <strong>
            {validation.remainingFen === null
              ? "待修正"
              : validation.remainingFen >= 0
                ? formatFen(validation.remainingFen)
                : `多分 ${formatFen(Math.abs(validation.remainingFen))}`}
          </strong>
        </div>
        <div>
          <span>已收</span>
          <strong>{formatFen(plan?.paidFen ?? 0)}</strong>
        </div>
      </div>

      {mode === "BY_ITEM" ? (
        <section className="checkout-split-item-balance" aria-label="商品分配检查">
          {order.items
            .filter((item) => activeQuantity(item) > 0)
            .map((item) => (
              <div key={item.id}>
                <span>
                  {item.name} · 共 {activeQuantity(item)} 件
                </span>
                <strong
                  className={
                    validation.itemRemaining[item.id] === 0 ? "is-balanced" : "is-unbalanced"
                  }
                >
                  {validation.itemRemaining[item.id] === null
                    ? "件数待修正"
                    : validation.itemRemaining[item.id] === 0
                      ? "已分完"
                      : (validation.itemRemaining[item.id] ?? 0) > 0
                        ? `还剩 ${validation.itemRemaining[item.id]} 件`
                        : `多分 ${Math.abs(validation.itemRemaining[item.id] ?? 0)} 件`}
                </strong>
              </div>
            ))}
        </section>
      ) : null}

      <div className="checkout-split-share-list">
        {editable.map((share, index) => {
          const saved = savedShareByKey.get(share.shareKey);
          const currentAmount = saved?.amountFen ?? validation.shares?.[index]?.amountFen ?? null;
          const paid = saved?.status === "PAID";
          const shareId = saved?.shareId;
          const method = shareId ? (methods[shareId] ?? "CASH") : "CASH";
          return (
            <article
              className={`checkout-split-share ${paid ? "is-paid" : ""}`}
              key={share.shareKey}
            >
              <div className="checkout-split-share-head">
                <strong>第 {index + 1} 份</strong>
                <span>{currentAmount === null ? "金额待修正" : formatFen(currentAmount)}</span>
                {paid ? (
                  <b>
                    <Check size={14} />
                    {paymentStateCopy(saved.paymentMethod)}
                  </b>
                ) : null}
              </div>
              <label>
                顾客称呼
                <input
                  value={share.payerLabel}
                  maxLength={30}
                  disabled={operationDisabled || locked}
                  onChange={(event) =>
                    updateShare(share.shareKey, { payerLabel: event.target.value })
                  }
                />
                <small>例如“1 号座小王”，付款时更容易核对。</small>
              </label>
              {mode === "BY_AMOUNT" ? (
                <label>
                  {share.payerLabel || `第 ${index + 1} 位顾客`}应付金额
                  <input
                    inputMode="decimal"
                    value={share.amountYuan}
                    disabled={operationDisabled || locked}
                    onChange={(event) =>
                      updateShare(share.shareKey, { amountYuan: event.target.value })
                    }
                  />
                  <small>单位：元，最多两位小数。</small>
                </label>
              ) : (
                <>
                  <div className="checkout-split-item-inputs">
                    {order.items
                      .filter((item) => activeQuantity(item) > 0)
                      .map((item) => (
                        <label key={item.id}>
                          {share.payerLabel || `第 ${index + 1} 位顾客`}分到{item.name}数量
                          <input
                            inputMode="numeric"
                            value={share.itemQuantities[item.id] ?? "0"}
                            disabled={operationDisabled || locked}
                            onChange={(event) =>
                              updateShare(share.shareKey, {
                                itemQuantities: {
                                  ...share.itemQuantities,
                                  [item.id]: event.target.value,
                                },
                              })
                            }
                          />
                          <small>
                            本单可分 {activeQuantity(item)} 件，每件 {formatFen(item.unitPriceFen)}
                            。
                          </small>
                        </label>
                      ))}
                  </div>
                  <label>
                    {share.payerLabel || `第 ${index + 1} 位顾客`}分到的台费、助教及其他金额
                    <input
                      inputMode="decimal"
                      value={share.otherAmountYuan}
                      disabled={operationDisabled || locked}
                      onChange={(event) =>
                        updateShare(share.shareKey, { otherAmountYuan: event.target.value })
                      }
                    />
                    <small>不属于商品行的费用在这里分配，单位：元。</small>
                  </label>
                </>
              )}

              {!locked ? (
                <div className="checkout-split-draft-actions">
                  {index > 0 ? (
                    <button
                      className="button secondary"
                      type="button"
                      disabled={operationDisabled || editable.length <= 2}
                      onClick={() => mergeIntoPrevious(index)}
                    >
                      <Merge size={15} />
                      并入上一位
                    </button>
                  ) : null}
                  <button
                    className="button secondary"
                    type="button"
                    disabled={operationDisabled || editable.length <= 2}
                    onClick={() =>
                      changeDraft(
                        editable.filter((candidate) => candidate.shareKey !== share.shareKey),
                      )
                    }
                  >
                    <Trash2 size={15} />
                    删除这份
                  </button>
                </div>
              ) : null}

              {saved && !paid ? (
                <div className="checkout-split-payment">
                  <label>
                    这位顾客怎么付款
                    <select
                      value={method}
                      disabled={operationDisabled}
                      onChange={(event) => {
                        const next = event.target.value as CheckoutSplitManualPaymentMethod;
                        setMethods((current) => ({ ...current, [saved.shareId]: next }));
                        setConfirmed((current) => ({ ...current, [saved.shareId]: false }));
                        setFeedback(null);
                      }}
                    >
                      <option value="CASH">现金</option>
                      <option value="STATIC_WECHAT">微信收款码</option>
                      <option value="STATIC_ALIPAY">支付宝收款码</option>
                    </select>
                  </label>
                  <p className={method === "CASH" ? "is-cash" : "is-code"}>
                    {method === "CASH"
                      ? `现金当面确认：请当面收取 ${formatFen(saved.amountFen)} 后再勾选确认。`
                      : `收款码待渠道核对：顾客扫码 ${formatFen(saved.amountFen)} 后只记录现场确认，不代表支付渠道已经对账。`}
                  </p>
                  <label className="checkout-split-confirm">
                    <input
                      type="checkbox"
                      checked={Boolean(confirmed[saved.shareId])}
                      disabled={operationDisabled}
                      onChange={(event) =>
                        setConfirmed((current) => ({
                          ...current,
                          [saved.shareId]: event.target.checked,
                        }))
                      }
                    />
                    <span>
                      {method === "CASH"
                        ? `我已当面收到 ${formatFen(saved.amountFen)} 现金`
                        : `我已看见顾客完成${paymentMethodLabels[method]}扫码，知道这笔仍要渠道核对`}
                    </span>
                  </label>
                  {dirty ? (
                    <div className="disabled-reason">当前分配有未保存修改，请先重新保存计划。</div>
                  ) : null}
                  <button
                    className="button primary"
                    type="button"
                    disabled={operationDisabled || dirty || !confirmed[saved.shareId]}
                    onClick={() => void payShare(saved.shareId)}
                  >
                    {method === "CASH" ? <Banknote size={16} /> : <CircleDollarSign size={16} />}
                    {payingShareId === saved.shareId
                      ? "正在确认这份收款..."
                      : method === "CASH"
                        ? "确认这份现金已收"
                        : "记录这份扫码收款"}
                  </button>
                </div>
              ) : null}
            </article>
          );
        })}
      </div>

      {!locked ? (
        <div className="checkout-split-plan-actions">
          <button
            className="button secondary"
            type="button"
            disabled={operationDisabled || editable.length >= 8}
            onClick={() =>
              changeDraft([
                ...editable,
                {
                  ...makeShares(order, mode, 1, [`顾客${editable.length + 1}`])[0],
                  amountYuan: "0.00",
                  otherAmountYuan: "0.00",
                  itemQuantities: Object.fromEntries(order.items.map((item) => [item.id, "0"])),
                } as EditableShare,
              ])
            }
          >
            <Plus size={16} />
            增加一位顾客
          </button>
          <button
            className="button primary"
            type="button"
            disabled={operationDisabled || validation.shares === null}
            onClick={() => void savePlan()}
          >
            <Save size={16} />
            {saving ? "正在保存分账计划..." : plan ? "重新保存分账计划" : "保存分账计划"}
          </button>
        </div>
      ) : null}

      {validation.messages.length > 0 && !locked ? (
        <div className="checkout-split-validation" role="alert">
          <strong>当前不能保存</strong>
          <ul>
            {validation.messages.map((message) => (
              <li key={message}>{message}</li>
            ))}
          </ul>
        </div>
      ) : null}

      {feedback ? (
        <output
          className={`checkout-split-feedback is-${feedback.kind.toLowerCase()}`}
          aria-live="assertive"
        >
          {feedback.message}
        </output>
      ) : null}

      {recoveryView.status !== "NONE" ? (
        <section className="checkout-split-recovery" aria-label="上一笔分账恢复">
          <div>
            <LockKeyhole size={17} />
            <span>
              <strong>上一笔分账操作尚未收口</strong>
              <small>
                {recoveryUi.message ??
                  "恢复记录仍在，本页不能修改分配、换付款方式或新建另一笔请求。"}
              </small>
            </span>
          </div>
          {recoveryView.status === "OWNED" ? (
            <p>{recoveryCommandCopy(recoveryView.recovery.command)}</p>
          ) : null}
          <div className="checkout-split-recovery-actions">
            {recoveryView.status === "OWNED" && recoveryView.canRetry ? (
              <button
                className="button primary"
                type="button"
                disabled={disabled || saving || payingShareId !== null || baselineStale}
                onClick={() => void dispatchRecovery(recoveryView.recovery)}
              >
                使用原请求安全重试
              </button>
            ) : null}
            {recoveryView.status === "OWNED" &&
            recoveryView.recovery.state === "OUTCOME_UNKNOWN" ? (
              <button
                className="button secondary"
                type="button"
                disabled={loading}
                onClick={() => void loadAuthoritativePlan()}
              >
                重新读取门店权威状态
              </button>
            ) : null}
          </div>
        </section>
      ) : null}

      {baselineStale ? (
        <div className="disabled-reason">
          分账付款已变化，正在读取付款后的最新台单；读取完成前不能收下一份。
        </div>
      ) : null}

      {disabled ? (
        <div className="disabled-reason">
          当前台单或门店写入状态不安全，多人分账只能查看，不能保存或收款。
        </div>
      ) : null}
    </section>
  );
}
